import {
    TaskActivity,
    TaskCommentModel,
    TaskPrefix,
    ColumnModelBase,
    QueryRequestData,
    TasksData,
    TaskLink,
    TaskLookupItem,
    ActionEvent,
    TaskError,
    TaskPreviewModel,
    PackageSetPreviewModel,
} from "../types";
import security from "../../common/services/SecurityService";
import {
    ColumnsFilter,
    TaskListLayoutResult,
    UserProfile,
} from "../../common/services/types";
import appClient, { ResultApi } from "../../common/services/AppClient";
import { UserModel } from "../../common/types/UserModel";
import {
    ActionDefinition,
    ClientActionData,
} from "../../administration/types/Actions";
import { TaskStatus } from "../../task_statuses/types";
import { endpoints } from "../../../config";

export default class TaskService {
    async getTasks(
        project: string | null,
        page: number,
        pageSize: number,
        searchTerm: string | null = null,
        excludeFilters: boolean,
        sortIndex: 1 | -1 = -1,
        sortField: string = "createDate",
        filters?: ColumnsFilter[],
    ) {
        const url = endpoints.tasksUrl + "tasks";
        const request = {
            project,
            page,
            pageSize,
            searchTerm,
            sortIndex,
            sortField,
            excludeFilters,
            filters: filters || null,
        };
        return appClient.post<TasksData>(url, request);
    }

    async searchTasksByUuid(
        projectId: string,
        uuid: string,
        uuidMetadataFieldId: string,
    ) {
        const uuidFilter: ColumnsFilter = {
            entityReference: uuidMetadataFieldId,
            fieldType: "Metadata",
            operator: "Equal",
            value: [uuid],
            extraFilters: {},
            isWidgetFilter: false,
        };
        return this.getTasks(projectId, 1, 100, null, true, -1, "createDate", [
            uuidFilter,
        ]);
    }

    async getTaskById(
        taskId: string | null,
        alphaId: string | null,
    ): Promise<ResultApi<TaskPreviewModel>> {
        const queryParams = taskId
            ? `?taskId=${taskId}`
            : `?alphaId=${alphaId}`;
        const url = `${endpoints.tasksUrl}tasks${queryParams}`;
        return appClient.get<TaskPreviewModel>(url);
    }

    async getTasksByGroupedWidgetId(
        widgetId: string,
        projectId: string | null,
        page: number,
        pageSize: number,
        searchTerm: string | undefined,
        sortField: string,
        sortIndex: number,
        filters: ColumnsFilter[],
    ) {
        const url = `${endpoints.tasksUrl}tasks/widget/${widgetId}`;
        const request = {
            projectId,
            page,
            pageSize,
            sortField,
            sortIndex,
            searchTerm: searchTerm || null,
            filters: filters || [],
        };
        return appClient.post<TasksData>(url, request);
    }

    async getTasksByPieChartSection(
        widgetId: string,
        projectId: string | null,
        value: string,
        page: number,
        pageSize: number,
        searchTerm: string | undefined,
        sortField: string,
        sortIndex: number,
    ) {
        let url = `${endpoints.tasksUrl}tasks/widget/${widgetId}/value/${encodeURIComponent(value)}?page=${page}&pageSize=${pageSize}&projectId=${projectId}&sortField=${encodeURIComponent(sortField)}&sortIndex=${sortIndex}`;
        url = searchTerm
            ? `${url}&searchTerm=${encodeURIComponent(searchTerm || "")}`
            : url;
        return appClient.get<TasksData>(url);
    }

    async getTasksByActivityType(
        activityType: string,
        projectId: string,
        period: string,
        page: number,
        pageSize: number,
        searchTerm: string | undefined,
        sortField: string,
        sortIndex: number,
    ) {
        let url = `${endpoints.tasksUrl}tasks/activity/${activityType}/period/${period}?page=${page}&pageSize=${pageSize}&projectId=${projectId}&sortField=${encodeURIComponent(sortField)}&sortIndex=${sortIndex}`;
        url = searchTerm
            ? `${url}&searchTerm=${encodeURIComponent(searchTerm || "")}`
            : url;
        return appClient.get<TasksData>(url);
    }

    async getTaskPrefixForProject(projectId: string) {
        const url = `${endpoints.tasksUrl}tasksettings/prefix/${encodeURIComponent(projectId)}`;
        return appClient.get<TaskPrefix | null>(url);
    }

    getUsersInProject(projectId: string) {
        const url =
            endpoints.tasksUrl +
            `users/project/${projectId}?includeRoles=false`;
        return appClient.get<UserProfile[]>(url);
    }

    getCommonUsersInProjects(projectIds: string[]) {
        const request = {
            projectIds,
        };
        const url =
            endpoints.tasksUrl + "users/project/users?includeRoles=false";
        return appClient.post<UserModel[]>(url, request);
    }

    getAllUsers() {
        const url = endpoints.tasksUrl + "userprofile";
        return appClient.get<UserProfile[]>(url);
    }

    async getPackageSet(packageSetId: string) {
        const url = `${endpoints.tasksUrl}packagesets/${encodeURIComponent(packageSetId)}/packages`;
        return appClient.get<PackageSetPreviewModel>(url);
    }

    async getTaskErrorsByTaskId(
        taskId: string,
    ): Promise<ResultApi<TaskError[]>> {
        const url = `${endpoints.tasksUrl}task-error/${taskId}`;
        return appClient.get<TaskError[]>(url);
    }

    updateFields(fields: { [field: string]: unknown }, taskId: string) {
        const request = {
            fields,
        };
        const url = `${endpoints.tasksUrl}tasks/${taskId}/fields`;
        return appClient.post(url, request);
    }

    updateFieldsBulk(fields: { [field: string]: unknown }, taskIds: string[]) {
        const request = {
            fields,
            taskIds,
        };
        const url = `${endpoints.tasksUrl}tasks/fields`;
        return appClient.post(url, request);
    }

    async analyzeAttachments(taskId: string, packageId: string) {
        const request = {
            taskId,
            packageId,
        };
        const url = endpoints.tasksUrl + "tasks/analyze";
        return appClient.post(url, request);
    }

    async getTaskComments(taskId: string, createDateAfter?: Date) {
        let url = `${endpoints.tasksUrl}taskcomments/task/${taskId}`;
        const params = new URLSearchParams();
        if (createDateAfter) {
            params.set("createDateAfter", createDateAfter.toISOString());
        }
        if (params.toString()) {
            url += `?${params.toString()}`;
        }

        const result = await appClient.get<TaskCommentModel[]>(url);
        if (result.isErr()) {
            return [];
        }

        return result.value ?? [];
    }

    createNewTask(formData: FormData) {
        const url = endpoints.tasksUrl + "tasks/new";
        return appClient.post<string>(url, formData);
    }

    appendAttachments(formData: FormData) {
        const url = endpoints.tasksUrl + "packagesets/attachment";
        return appClient.post(url, formData);
    }

    removeAttachment(taskId: string, packageId: string) {
        const url =
            endpoints.tasksUrl +
            `packagesets/attachment/${taskId}/${packageId}`;
        return appClient.update(url);
    }

    removeAttachments(taskId: string, packageIds: string[]) {
        const url =
            endpoints.tasksUrl + `packagesets/attachment/${taskId}/bulk`;
        return appClient.update(url, { packageIds });
    }

    addComment(taskId: string, body: string) {
        const request = {
            taskId,
            body,
        };
        const url = endpoints.tasksUrl + "taskcomments/new";
        return appClient.post(url, request);
    }

    updateComment(commentId: string, body: string) {
        const request = {
            commentId,
            body,
        };
        const url = endpoints.tasksUrl + "taskcomments";
        return appClient.update(url, request);
    }

    deleteComment(commentId: string) {
        const url = endpoints.tasksUrl + `taskcomments/delete/${commentId}`;
        return appClient.post(url);
    }

    async getTaskStatuses(typeId?: string) {
        let url = `${endpoints.tasksUrl}taskstatuses`;
        if (typeId) {
            url += `/type/${typeId}`;
        }
        const resp = await appClient.get<TaskStatus[]>(url);
        return resp.mapErr((err) => new Error(err.data?.title));
    }

    handleDownload(ids: string[]) {
        return security.invoke((token) => {
            for (const id of ids) {
                const path = encodeURIComponent(id);
                const t = "?access_token=" + encodeURIComponent(token);
                let url = "";
                url = `${endpoints.tasksUrl}document/${path}${t}`;

                const downloadWindow = window.open(url);

                if (!downloadWindow) {
                    alert(
                        "You must allow pop-ups for download to work properly.",
                    );
                }
            }
            return Promise.resolve();
        });
    }

    async getTaskActivities(taskId: string): Promise<TaskActivity[]> {
        const url = `${endpoints.tasksUrl}tasks/${encodeURIComponent(taskId)}/activities`;
        const result = await appClient.get<TaskActivity[]>(url);
        if (result.isErr()) {
            return [];
        }

        return result.value ?? [];
    }

    getUserProfilePicture(userId: string) {
        const url = `${endpoints.tasksUrl}userProfile/icon/${userId}`;
        return appClient.get<Blob>(url, "blob");
    }

    toggleTaskFollowing(taskId: string) {
        const url = endpoints.tasksUrl + `tasks/togglefollow/${taskId}`;
        return appClient.update<string[]>(url);
    }

    readTextTaskAttachment(attachmentId: string) {
        const url = endpoints.tasksUrl + `tasks/readtext/${attachmentId}`;
        return appClient.get<string>(url, "text");
    }

    assignTasksToUser(taskIds: string[], userId: string) {
        const request = {
            taskIds,
            userId,
        };
        const url = endpoints.tasksUrl + "tasks/assignmultiple";
        return appClient.post(url, request);
    }

    emailTaskToUsers(taskId: string, emailAddresses: string[]) {
        const request = {
            taskId,
            emailAddresses,
        };
        const url = endpoints.tasksUrl + "notifications/send-task-to-users";
        return appClient.post<string[]>(url, request);
    }

    saveLayoutConfig(
        projectId: string,
        isMetaView: boolean,
        columns: ColumnModelBase[],
    ) {
        const request = {
            columns,
            projectId,
            isMetaView,
        };
        const url = endpoints.tasksUrl + "tasks/layout";
        return appClient.post<string>(url, request);
    }

    pinTask(taskId: string) {
        const url = `${endpoints.tasksUrl}tasks/pin/task/${taskId}`;
        return appClient.post<string>(url);
    }

    unpinTask(taskId: string) {
        const url = `${endpoints.tasksUrl}tasks/unpin/task/${taskId}`;
        return appClient.post<string>(url);
    }

    deleteLayoutConfig() {
        const url = endpoints.tasksUrl + "tasks/layout/reset";
        return appClient.post(url);
    }

    async getTaskListLayoutForUser() {
        const url = `${endpoints.tasksUrl}tasks/layout`;
        return appClient.get<TaskListLayoutResult>(url);
    }

    async saveSubTask(name: string, taskId: string) {
        const request = {
            name,
        };
        const url = `${endpoints.tasksUrl}task/${taskId}/subtasks`;
        return await appClient.post<string>(url, request);
    }

    queryMessage(taskId: string, requestData: QueryRequestData) {
        const url = `${endpoints.tasksUrl}emessage/query/task-id/${taskId}`;
        return appClient.post(url, requestData);
    }

    acceptMessage(taskId: string) {
        const url = `${endpoints.tasksUrl}emessage/accept/task-id/${taskId}`;
        return appClient.post(url);
    }

    rejectMessage(taskId: string) {
        const url = `${endpoints.tasksUrl}emessage/reject/task-id/${taskId}`;
        return appClient.post(url);
    }

    getAvailableActions(taskId: string) {
        const url = `${endpoints.tasksUrl}tasks/${taskId}/actions`;
        return appClient.get<ActionDefinition[]>(url);
    }

    executeAction(
        taskId: string,
        actionId: string,
        payload: unknown,
        packageId?: string,
    ) {
        const url = `${endpoints.tasksUrl}tasks/${taskId}/action/${actionId}`;
        return appClient.post(url, { fields: payload, packageId });
    }

    prepareClientAction(
        taskId: string,
        actionId: string,
        payload: unknown,
        packageId?: string,
    ) {
        const url = `${endpoints.tasksUrl}tasks/${taskId}/action/client/${actionId}`;
        return appClient.post<ClientActionData>(url, {
            fields: payload,
            packageId,
        });
    }

    getActionEvents(taskId: string) {
        const url = `${endpoints.tasksUrl}tasks/${taskId}/actions/events`;
        return appClient.get<ActionEvent[]>(url);
    }

    updateMetadataField(taskId: string, fieldName: string, value: unknown) {
        const url = `${endpoints.tasksUrl}tasks/${taskId}/metadata/${fieldName}`;
        return appClient.post(url, value);
    }

    updateColumnFilters(
        projectId: string | null,
        pageSize: number,
        page: number,
        filters: ColumnsFilter[],
        searchTerm: string | undefined,
        sortField: string = "createDate",
        sortIndex: 1 | -1 = -1,
        widgetId: string | undefined,
        widgetValue: string | undefined,
    ) {
        const url = `${endpoints.tasksUrl}tasks/change/filter`;
        const requestFilters = filters.map((f) => ({
            entityReference: f.entityReference,
            fieldType: f.fieldType,
            value: f.value,
            operator: f.operator,
            extraFilters: f.extraFilters || null,
        }));

        const request = {
            projectId,
            pageSize,
            page,
            searchTerm,
            sortField,
            sortIndex,
            widgetId,
            widgetValue,
            filters: requestFilters,
        };
        return appClient.post<TasksData>(url, request);
    }

    resetAllColumnFilters(
        projectId: string | null,
        pageSize: number,
        page: number,
        searchTerm: string | undefined,
        sortField: string = "createDate",
        sortIndex: 1 | -1 = -1,
    ) {
        const url = `${endpoints.tasksUrl}tasks/reset/filters`;
        const request = {
            projectId,
            pageSize,
            page,
            searchTerm,
            sortField,
            sortIndex,
        };
        return appClient.post<TasksData>(url, request);
    }

    getTaskLinks(taskId: string) {
        const url = `${endpoints.tasksUrl}tasklinks/task/${taskId}`;
        return appClient.get<TaskLink[]>(url);
    }

    createTaskLink(formData: FormData) {
        const url = `${endpoints.tasksUrl}tasklinks`;
        return appClient.post<string>(url, formData);
    }

    deleteTaskLink(linkId: string) {
        const url = `${endpoints.tasksUrl}tasklinks/${linkId}`;
        return appClient.delete<string>(url);
    }

    lookupTasks(projectId: string, searchTerm: string, currentTaskId?: string) {
        let url = `${endpoints.tasksUrl}tasks/lookup?projectId=${projectId}&query=${searchTerm}`;

        if (currentTaskId) {
            url += `&currentTaskId=${currentTaskId}`;
        }

        return appClient.get<TaskLookupItem[]>(url);
    }

    cloneTask(id: string) {
        const url = `${endpoints.tasksUrl}tasks/${id}/clone`;
        return appClient.post<TaskPreviewModel>(url);
    }
}
